import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { getCollectorSuppliers } from '@/services/api/collector';

interface SupplierTypeaheadProps {
  value: string;
  onSelect: (supplierId: string, supplierName: string) => void;
  disabled?: boolean;
  // Optional display label for currently selected value (helps when results aren't loaded)
  selectedLabel?: string;
}

type NormalizedSupplier = { id: string; name: string; shortName?: string; logo?: string; logoUrl?: string };

export function SupplierTypeahead({ value, onSelect, disabled, selectedLabel }: SupplierTypeaheadProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<NormalizedSupplier[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const debounceRef = useRef<number | undefined>(undefined);

  const minChars = 2;

  // Debounced server-side search
  useEffect(() => {
    // Only search when popover is open and query is long enough
    if (!open) return;
    if (searchQuery.trim().length < minChars) {
      setResults([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Debounce network calls by ~400ms
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const list = await getCollectorSuppliers(searchQuery.trim());
        const mapped: NormalizedSupplier[] = (list as any[] || []).map((s: any) => {
          const id = String(s.SupplierId ?? s.supplierId ?? s.id ?? s.code ?? s.SupplierName ?? s.supplierName);
          const name = s.SupplierName || s.supplierName || s.name || s.portalName || s.code || '';
          const shortName = s.SupplierShortName || s.supplierShortName || s.shortName || s.ShortName;
          const logo = s.logo || s.logoUrl || s.LogoUrl || s.imageUrl || undefined;
          const logoUrl = s.logoUrl || s.LogoUrl || undefined;
          return { id, name, shortName, logo, logoUrl };
        });
        setResults(mapped);
      } catch (e: any) {
        setError(e instanceof Error ? e : new Error('Failed to load suppliers'));
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 400);

    return () => {
      window.clearTimeout(debounceRef.current);
    };
  }, [open, searchQuery]);

  // Try to resolve the selected supplier from current results
  const selectedSupplier = useMemo(() => {
    return results.find((s) => String(s.id) === String(value));
  }, [results, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          {selectedSupplier ? (
            <div className="flex items-center gap-2">
              {((selectedSupplier as any).logo || (selectedSupplier as any).logoUrl) && (
                <img
                  src={(selectedSupplier as any).logo || (selectedSupplier as any).logoUrl}
                  alt={selectedSupplier.name}
                  className="h-5 w-5 object-contain"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              )}
              <span>{selectedSupplier.name}</span>
            </div>
          ) : selectedLabel ? (
            <div className="flex items-center gap-2">
              <span>{selectedLabel}</span>
            </div>
          ) : (
            'Select supplier...'
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[320px] sm:w-[360px] max-h-[460px] overflow-hidden" align="start" side="bottom">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search suppliers (min 2 chars)..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList className="max-h-[380px] overflow-y-auto">
            {error && (
              <div className="p-4 text-sm text-destructive border-l-2 border-destructive/50 bg-destructive/10 m-2 rounded">
                {error.message.includes('Authentication') || error.message.includes('log in')
                  ? '🔒 Please log in to search suppliers' 
                  : '❌ Failed to load suppliers. Please try again.'}
              </div>
            )}
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading suppliers...</span>
              </div>
            )}
            {!isLoading && !error && searchQuery.length < minChars && (
              <CommandEmpty>
                <div className="text-center py-4">
                  <div className="text-sm text-muted-foreground">Type at least 2 characters to search</div>
                </div>
              </CommandEmpty>
            )}
            {!isLoading && !error && searchQuery.length >= minChars && results.length === 0 && (
              <CommandEmpty>
                <div className="text-center py-4">
                  <div className="text-sm text-muted-foreground">No suppliers found for "{searchQuery}"</div>
                  <div className="text-xs text-muted-foreground mt-1">Try a different search term</div>
                </div>
              </CommandEmpty>
            )}
            {!isLoading && !error && results.length > 0 && (
              <CommandGroup>
                {results.map((supplier: any) => (
                  <CommandItem
                    key={supplier.id}
                    value={supplier.id}
                    onSelect={() => {
                      onSelect(String(supplier.id), supplier.name);
                      setOpen(false);
                    }}
                    className="flex items-center gap-2 cursor-pointer hover:bg-accent"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 flex-shrink-0',
                        value === supplier.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {(supplier.logo || supplier.logoUrl) && (
                      <img
                        src={supplier.logo || supplier.logoUrl}
                        alt={supplier.name}
                        className="h-5 w-5 object-contain flex-shrink-0"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-medium truncate">{supplier.name}</span>
                      {supplier.shortName && (
                        <span className="text-xs text-muted-foreground truncate">{supplier.shortName}</span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
